import type {
  Message,
  UserMessageContent,
} from "@redotech/redo-agents-common/llm-messages";
import type { AskRedoUIMessage } from "../types";

/**
 * Convert outgoing `UIMessage`s (from `useChat.sendMessage()`) into
 * Redo's `Message[]` shape expected by the RPC input.
 *
 * `useChat` passes the full message history on every turn, but the
 * server already has everything except the newest user message —
 * `LlmThread.messages` is the persistence layer, keyed by threadId.
 * Sending the full history would duplicate old user messages on the
 * thread and cause the agent to re-answer previous questions.
 *
 * So we emit just the **last user message**. Anything older is
 * already on the thread. Assistant / tool / system UIMessages are
 * never forwarded — the agent produces those on the server.
 */
export function extractLastUserMessage(
  messages: AskRedoUIMessage[],
): Message[] {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (!m || m.role !== "user") {
      continue;
    }
    const content = userPartsToContent(m.parts);
    if (content === undefined) {
      return [];
    }
    return [{ id: m.id, role: "user", content }];
  }
  return [];
}

type AskRedoMessagePart = AskRedoUIMessage["parts"][number];

function userPartsToContent(
  parts: readonly AskRedoMessagePart[],
): string | UserMessageContent[] | undefined {
  const converted: UserMessageContent[] = [];
  for (const part of parts) {
    if (part.type === "text") {
      if (part.text.length > 0) {
        converted.push({ type: "text", text: part.text });
      }
      continue;
    }
    if (part.type === "file") {
      if (typeof part.url !== "string") {
        continue;
      }
      if (part.mediaType?.startsWith("image/")) {
        converted.push({ type: "image", image: part.url });
      } else {
        converted.push({
          type: "file",
          data: part.url,
          mediaType: part.mediaType ?? "application/octet-stream",
          ...(part.filename ? { filename: part.filename } : {}),
        });
      }
      continue;
    }
    // Reasoning / tool / data / source parts never appear on user
    // messages, so we drop anything else silently.
  }

  if (converted.length === 0) {
    return undefined;
  }
  // Collapse a single text-only message to plain string — matches the
  // shape every other Redo agent produces and keeps thread history
  // consistent.
  if (converted.length === 1 && converted[0]?.type === "text") {
    return converted[0].text;
  }
  return converted;
}

/**
 * Detect a pending tool-approval response on the last assistant
 * message.
 *
 * `useChat`'s `addToolApprovalResponse(...)` does NOT add a new
 * `type: "tool-approval-response"` part — it mutates the existing
 * tool part in place, transitioning it from `"approval-requested"`
 * to `"approval-responded"` and setting `approval.approved`.
 *
 * So the signal to resume is: a `DynamicToolUIPart` with
 *   state === "approval-responded"
 * on the last assistant message, carrying `approval.approved`.
 * The server reads this via the RPC `approvalResponse` param and
 * flips `PendingTool.confirmed` before resuming the agent.
 */
export function extractApprovalResponse(
  messages: AskRedoUIMessage[],
): { toolCallId: string; approved: boolean; reason?: string } | undefined {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (!m || m.role !== "assistant") {
      continue;
    }
    // A card the server parked again can leave its earlier answer's part
    // behind; only a tool call's newest part counts.
    const seenToolCallIds = new Set<string>();
    for (const part of [...m.parts].reverse()) {
      if (
        part.type !== "dynamic-tool" ||
        seenToolCallIds.has(part.toolCallId)
      ) {
        continue;
      }
      seenToolCallIds.add(part.toolCallId);
      if (isApprovalRespondedPart(part)) {
        return {
          toolCallId: part.toolCallId,
          approved: part.approval.approved,
          reason: part.approval.reason,
        };
      }
    }
    return undefined;
  }
  return undefined;
}

/**
 * Narrow a `UIMessagePart` to a dynamic tool part in the
 * `approval-responded` state. We don't import `DynamicToolUIPart`'s
 * narrowed sub-types from the SDK because they aren't exported by
 * name; the `state` discriminant on the broad union is enough.
 */
function isApprovalRespondedPart(
  part: AskRedoMessagePart,
): part is AskRedoMessagePart & {
  type: "dynamic-tool";
  toolCallId: string;
  state: "approval-responded";
  approval: { id: string; approved: boolean; reason?: string };
} {
  return part.type === "dynamic-tool" && part.state === "approval-responded";
}
